/**
 * Utility functions for Challenge Page
 */

/**
 * Format date for display in checkpoint cards
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date string
 */
export const formatCheckpointDate = (dateString) => {
  if (!dateString) return "";

  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, "0");
  const month = date.toLocaleDateString("en-US", { month: "short" });
  const time = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
  return `${day} ${month}, ${time}`;
};

/**
 * Get status icon for checkpoint
 * @param {boolean} isCompleted - Whether checkpoint is completed
 * @param {boolean} isLocked - Whether checkpoint is locked
 * @returns {string} Icon character
 */
export const getCheckpointIcon = (isCompleted, isLocked) => {
  if (isCompleted) return "✓";
  if (isLocked) return "🔒";
  return "▶";
};

/**
 * Calculate progress percentage
 * @param {number} completed - Number of completed checkpoints
 * @param {number} total - Total number of checkpoints
 * @returns {number} Progress percentage (0-100)
 */
export const calculateProgress = (completed, total) => {
  if (!total || total === 0) return 0;
  return Math.round((completed / total) * 100);
};

/**
 * Get challenge status based on completion
 * @param {number} progressPercentage - Progress percentage
 * @returns {object} Status object with type and message
 */
export const getChallengeStatus = (progressPercentage) => {
  if (progressPercentage === 0) {
    return {
      type: "not-started",
      message: "Ready to begin your journey!",
      color: "#64748b",
    };
  } else if (progressPercentage < 100) {
    return {
      type: "in-progress",
      message: "Keep going, you're doing great!",
      color: "#7c3aed",
    };
  } else {
    return {
      type: "completed",
      message: "Congratulations! Challenge completed!",
      color: "#22c55e",
    };
  }
};

/**
 * Safe HTML parser for API content
 * @param {string} htmlString - HTML string from API
 * @returns {object} Props for dangerouslySetInnerHTML
 */
export const safeParseHTML = (htmlString) => {
  if (!htmlString) return { __html: "" };

  // Basic sanitization - remove script tags and on* attributes
  const sanitized = htmlString
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "")
    .replace(/\son\w+\s*=\s*["'][^"']*["']/gi, "");

  return { __html: sanitized };
};

/**
 * Generate animation delay for staggered animations
 * @param {number} index - Item index
 * @param {number} baseDelay - Base delay in seconds
 * @param {number} increment - Increment per item in seconds
 * @returns {string} CSS animation delay
 */
export const getAnimationDelay = (index, baseDelay = 0, increment = 0.1) => {
  return `${baseDelay + index * increment}s`;
};

/**
 * Debounce function for performance optimization
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} Debounced function
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Check if user is on mobile device
 * @returns {boolean} True if mobile device
 */
export const isMobile = () => {
  return window.innerWidth <= 768;
};

/**
 * Smooth scroll to element
 * @param {string} elementId - ID of element to scroll to
 * @param {number} offset - Offset from top in pixels
 */
export const scrollToElement = (elementId, offset = 0) => {
  const element = document.getElementById(elementId);
  if (element) {
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });
  }
};

/**
 * Handle checkpoint action based on status
 * @param {object} checkpoint - Checkpoint data
 * @param {Function} onStart - Callback for start action
 * @param {Function} onView - Callback for view action
 */
export const handleCheckpointAction = (checkpoint, onStart, onView) => {
  if (checkpoint.status === "locked") {
    return; // Do nothing for locked checkpoints
  }

  if (checkpoint.is_completed) {
    onView?.(checkpoint);
  } else {
    onStart?.(checkpoint);
  }
};

/**
 * Get readable time remaining or elapsed
 * @param {string} startDate - Challenge start date
 * @param {string} endDate - Challenge end date
 * @returns {string} Human readable time string
 */
export const getTimeStatus = (startDate, endDate) => {
  const now = new Date();
  const start = new Date(startDate);
  const end = new Date(endDate);

  if (now < start) {
    const daysToStart = Math.ceil((start - now) / (1000 * 60 * 60 * 24));
    return `Starts in ${daysToStart} day${daysToStart !== 1 ? "s" : ""}`;
  } else if (now >= start && now <= end) {
    const daysRemaining = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return `${daysRemaining} day${daysRemaining !== 1 ? "s" : ""} remaining`;
  } else {
    return "Challenge completed";
  }
};

/**
 * Validate API response structure
 * @param {object} data - API response data
 * @param {Array} requiredFields - Required fields array
 * @returns {boolean} True if valid
 */
export const validateApiResponse = (data, requiredFields) => {
  if (!data || typeof data !== "object") return false;

  return requiredFields.every((field) => {
    return data.hasOwnProperty(field) && data[field] !== undefined;
  });
};

export default {
  formatCheckpointDate,
  getCheckpointIcon,
  calculateProgress,
  getChallengeStatus,
  safeParseHTML,
  getAnimationDelay,
  debounce,
  isMobile,
  scrollToElement,
  handleCheckpointAction,
  getTimeStatus,
  validateApiResponse,
};
